import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Header from '../components/Header';
import fallbackCoinIcon from '../assets/biticon.png';
import showStore from '../stores/showStore';
import {
  formatCompactNumber,
  formatCurrency,
  formatPercent,
  formatSupply,
  summarizeDescription,
} from '../helpers/formatters';

export default function Show() {
  const params = useParams();
  const coin = showStore((state) => state.coin);
  const graphData = showStore((state) => state.graphData);
  const loading = showStore((state) => state.loading);
  const error = showStore((state) => state.error);
  const notice = showStore((state) => state.notice);
  const fetchData = showStore((state) => state.fetchData);
  const reset = showStore((state) => state.reset);

  React.useEffect(() => {
    fetchData(params.id);

    return () => {
      reset();
    };
  }, [fetchData, params.id, reset]);

  const marketData = coin?.market_data;
  const links = [
    {
      label: 'Official website',
      value: coin?.links?.homepage?.find(Boolean),
    },
    {
      label: 'Twitter',
      value: coin?.links?.twitter_screen_name
        ? `https://twitter.com/${coin.links.twitter_screen_name}`
        : null,
    },
    {
      label: 'Blockchain explorers',
      value: coin?.links?.blockchain_site?.find(Boolean),
    },
  ].filter((link) => link.value);

  return (
    <div className="page-shell" id="top">
      <Header back />

      <main className="page-main">
        <div className="width detail-layout">
          {loading && !coin && (
            <div className="detail-loading">
              <div className="detail-hero skeleton" aria-hidden="true" />
              <div className="detail-panel skeleton" aria-hidden="true" />
            </div>
          )}

          {error && (
            <div className="status-banner status-banner--error">
              <span>{error}</span>
              <button type="button" className="ghost-button" onClick={() => fetchData(params.id)}>
                Retry
              </button>
            </div>
          )}

          {notice && <div className="status-banner status-banner--info">{notice}</div>}

          {coin && (
            <>
              <section className="detail-hero">
                <div className="detail-hero__identity">
                  <img
                    src={coin.image?.large || coin.image?.small}
                    alt={coin.name}
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = fallbackCoinIcon;
                    }}
                  />
                  <div>
                    <p className="eyebrow">Coin details</p>
                    <h2>{coin.name}</h2>
                    <p>{(coin.symbol || '').toUpperCase()}</p>
                  </div>
                </div>

                <div className="detail-hero__summary">
                  <strong>{formatCurrency(marketData?.current_price?.usd)}</strong>
                  <span
                    className={
                      marketData?.price_change_percentage_24h >= 0 ? 'green-text' : 'red-text'
                    }
                  >
                    {formatPercent(marketData?.price_change_percentage_24h)}
                  </span>
                  <small>Market cap {formatCompactNumber(marketData?.market_cap?.usd)}</small>
                </div>
              </section>

              <section className="detail-panel">
                <div className="detail-panel__header">
                  <div>
                    <p className="eyebrow">Chart</p>
                    <h3>Last 30 days</h3>
                  </div>
                </div>

                <div className="chart-shell">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={graphData}
                      margin={{
                        top: 10,
                        right: 20,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <defs>
                        <linearGradient id="coinzChart" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0f766e" stopOpacity={0.38} />
                          <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(19, 42, 38, 0.08)" vertical={false} />
                      <XAxis dataKey="date" tickLine={false} axisLine={false} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => formatCurrency(value, { notation: 'compact' })}
                      />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Price']}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDate || ''}
                        contentStyle={{
                          borderRadius: 18,
                          border: '1px solid rgba(19, 42, 38, 0.08)',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="price"
                        stroke="#0f766e"
                        strokeWidth={3}
                        fill="url(#coinzChart)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </section>

              <section className="detail-stats">
                <div className="stat-card">
                  <span>Market cap rank</span>
                  <strong>{marketData?.market_cap_rank ? `#${marketData.market_cap_rank}` : '--'}</strong>
                </div>
                <div className="stat-card">
                  <span>24h high</span>
                  <strong>{formatCurrency(marketData?.high_24h?.usd)}</strong>
                </div>
                <div className="stat-card">
                  <span>24h low</span>
                  <strong>{formatCurrency(marketData?.low_24h?.usd)}</strong>
                </div>
                <div className="stat-card">
                  <span>Circulating supply</span>
                  <strong>{formatSupply(marketData?.circulating_supply)}</strong>
                </div>
                <div className="stat-card">
                  <span>Total supply</span>
                  <strong>{formatSupply(marketData?.total_supply)}</strong>
                </div>
                <div className="stat-card">
                  <span>1 year change</span>
                  <strong
                    className={
                      marketData?.price_change_percentage_1y >= 0 ? 'green-text' : 'red-text'
                    }
                  >
                    {formatPercent(marketData?.price_change_percentage_1y)}
                  </strong>
                </div>
              </section>

              <section className="detail-columns">
                <article className="detail-panel">
                  <div className="detail-panel__header">
                    <div>
                      <p className="eyebrow">About</p>
                      <h3>Description</h3>
                    </div>
                  </div>
                  <p className="detail-copy">{summarizeDescription(coin.description?.en)}</p>
                </article>

                <article className="detail-panel">
                  <div className="detail-panel__header">
                    <div>
                      <p className="eyebrow">Links</p>
                      <h3>Official sources</h3>
                    </div>
                  </div>

                  {links.length ? (
                    <div className="link-list">
                      {links.map((link) => (
                        <a key={link.label} href={link.value} target="_blank" rel="noreferrer">
                          <span>{link.label}</span>
                          <span>Open</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="detail-copy">No public links were provided for this coin.</p>
                  )}
                </article>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
